import { FC, ReactNode, useEffect } from 'react';

import { useForm } from '@inertiajs/react';
import { AlertCircle, Info } from 'lucide-react';

import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/Dialog';
import { Label } from '../../../components/ui/Label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import {
    ORDER_REVIEW_COMMENT_MAX_LENGTH,
    getOrderReviewRatingLabel,
    ORDER_REVIEW_COMPLAINT_OPTIONS,
    ORDER_REVIEW_PICKUP_HIDDEN_COMPLAINT_VALUES,
    ORDER_REVIEW_RATING_OPTIONS,
} from '../../../models/orderReview';
import { profileOrderActionRoute } from '../../../models/routes';
import { Order, OrderReviewRating } from '../../../types';

type ReviewFormData = {
    overall_rating: OrderReviewRating | '';
    item_description_rating: OrderReviewRating | '';
    packaging_rating: OrderReviewRating | '';
    comment: string;
    complaints: string[];
    source: '' | 'profile_reviews';
};

interface Props {
    order: Order | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    source?: 'profile_reviews';
}

const EMPTY_FORM: ReviewFormData = {
    overall_rating: '',
    item_description_rating: '',
    packaging_rating: '',
    comment: '',
    complaints: [],
    source: '',
};

const firstError = (error?: string | string[]): string => {
    if (Array.isArray(error)) {
        return error[0] || '';
    }

    return error || '';
};

const ReviewSelectField: FC<{
    id: keyof Pick<
        ReviewFormData,
        'overall_rating' | 'item_description_rating' | 'packaging_rating'
    >;
    label: string;
    description: string;
    value: ReviewFormData['overall_rating'];
    error?: string | string[];
    onChange: (value: OrderReviewRating) => void;
    compact?: boolean;
}> = ({ id, label, description, value, error, onChange, compact = false }) => {
    const errorMessage = firstError(error);

    return (
        <div className="space-y-2">
            {!compact && (
                <div className="space-y-1">
                    <Label htmlFor={id}>{label}</Label>
                    <p className="text-sm text-muted-foreground">
                        {description}
                    </p>
                </div>
            )}

            <Select
                value={value || undefined}
                onValueChange={(nextValue: OrderReviewRating) =>
                    onChange(nextValue)
                }
            >
                <SelectTrigger
                    id={id}
                    className={`h-9 min-h-9 py-1.5 text-sm ${
                        errorMessage ? 'border-red-500' : ''
                    }`}
                >
                    <SelectValue placeholder="Оберіть оцінку" />
                </SelectTrigger>
                <SelectContent>
                    {ORDER_REVIEW_RATING_OPTIONS.map((option) => (
                        <SelectItem
                            key={option.value}
                            value={option.value}
                            className="text-sm"
                        >
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {errorMessage && (
                <p className="text-sm text-red-600">{errorMessage}</p>
            )}
        </div>
    );
};

const ReviewFieldRow: FC<{
    id: string;
    label: string;
    description?: string;
    children: ReactNode;
}> = ({ id, label, description, children }) => {
    return (
        <div className="grid gap-3 border-b border-slate-200/80 pb-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start lg:gap-6">
            <div className="space-y-1">
                <Label htmlFor={id} className="text-base font-semibold">
                    {label}
                </Label>
                {description && (
                    <p className="text-sm text-muted-foreground italic">
                        {description}
                    </p>
                )}
            </div>

            <div>{children}</div>
        </div>
    );
};

export const OrderReviewDialog: FC<Props> = ({
    order,
    open,
    onOpenChange,
    source,
}) => {
    const { data, setData, post, processing, errors, clearErrors, reset } =
        useForm<ReviewFormData>({
            ...EMPTY_FORM,
            source: source || '',
        });
    const formErrors = errors as Partial<
        Record<keyof ReviewFormData | 'general', string | string[]>
    >;
    const isPickup = order?.delivery?.method === 'pickup';

    useEffect(() => {
        if (!open) return;

        reset();
        clearErrors();
    }, [clearErrors, open, order?.id, reset]);

    if (!order) return null;

    const closeDialog = () => {
        if (processing) return;

        clearErrors();
        reset();
        onOpenChange(false);
    };

    const toggleComplaint = (
        complaint: string,
        checked: boolean | 'indeterminate',
    ) => {
        const nextComplaints =
            checked === true
                ? [...data.complaints, complaint]
                : data.complaints.filter((value) => value !== complaint);

        setData('complaints', nextComplaints);
    };

    const handleSubmit = () => {
        post(profileOrderActionRoute(order.id, 'review'), {
            preserveScroll: true,
            onSuccess: () => {
                closeDialog();
            },
        });
    };

    const generalError = firstError(formErrors.general);
    const complaintsError = firstError(formErrors.complaints);
    const commentLengthLabel = `${data.comment.length}/${ORDER_REVIEW_COMMENT_MAX_LENGTH}`;
    const complaintOptions = ORDER_REVIEW_COMPLAINT_OPTIONS.filter(
        (option) =>
            !isPickup ||
            !ORDER_REVIEW_PICKUP_HIDDEN_COMPLAINT_VALUES.includes(option.value),
    );
    const selectedRatingsSummary = [
        `Обрано: ${getOrderReviewRatingLabel(data.overall_rating || null)}`,
        `Опис: ${getOrderReviewRatingLabel(
            data.item_description_rating || null,
        )}`,
        !isPickup
            ? `Пакування: ${getOrderReviewRatingLabel(
                  data.packaging_rating || null,
              )}`
            : null,
    ]
        .filter((value): value is string => Boolean(value))
        .join(' • ');

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    closeDialog();
                    return;
                }

                onOpenChange(true);
            }}
        >
            <DialogContent className="max-h-[90vh] overflow-y-auto p-0 sm:max-w-5xl">
                <DialogHeader className="border-b px-6 py-5">
                    <DialogTitle>Оцініть замовлення {order.number}</DialogTitle>
                    <DialogDescription>
                        {isPickup
                            ? 'Поділіться чесним відгуком про весь досвід покупки та опис товару.'
                            : 'Поділіться чесним відгуком про весь досвід покупки, опис товару та якість пакування.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 px-6">
                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_290px] lg:items-start">
                        <div className="space-y-6">
                            <Alert
                                variant="info"
                                className="rounded-lg border-blue-300 bg-cyan-50 text-cyan-800"
                            >
                                <Info />
                                <AlertTitle>
                                    Почніть із діалогу з продавцем
                                </AlertTitle>
                                <AlertDescription className="text-cyan-700">
                                    Якщо виникла проблема, зазвичай краще спершу
                                    написати продавцю перед тим, як залишати
                                    негативний відгук.
                                </AlertDescription>
                            </Alert>

                            {/*<aside className="rounded-lg bg-slate-100 p-4 text-sm leading-7 text-slate-700">*/}
                            {/*    <p>*/}
                            {/*        Будь ласка, залишайте об’єктивний відгук і*/}
                            {/*        описуйте саме факти, особливо якщо виникла*/}
                            {/*        проблема.*/}
                            {/*    </p>*/}
                            {/*    <p className="mt-4">*/}
                            {/*        Оцінюйте товар і пакування окремо. Навіть якщо*/}
                            {/*        сам товар не відповідав очікуванням, якісне*/}
                            {/*        пакування не варто позначати як погане без*/}
                            {/*        причини.*/}
                            {/*    </p>*/}
                            {/*</aside>*/}

                            <div className="space-y-5">
                                <ReviewFieldRow
                                    id="overall_rating"
                                    label="Загальна оцінка"
                                    description="Наскільки ви задоволені всією покупкою?"
                                >
                                    <ReviewSelectField
                                        id="overall_rating"
                                        label="Загальна оцінка"
                                        value={data.overall_rating}
                                        error={formErrors.overall_rating}
                                        onChange={(value) =>
                                            setData('overall_rating', value)
                                        }
                                        description=""
                                        compact
                                    />
                                </ReviewFieldRow>

                                <ReviewFieldRow id="comment" label="Коментар">
                                    <div className="space-y-2">
                                        <Textarea
                                            id="comment"
                                            value={data.comment}
                                            onChange={(event) =>
                                                setData(
                                                    'comment',
                                                    event.target.value,
                                                )
                                            }
                                            maxLength={
                                                ORDER_REVIEW_COMMENT_MAX_LENGTH
                                            }
                                            placeholder="Опишіть, що сподобалось або що було не так."
                                            className={`min-h-[92px] resize-none rounded-xl ${
                                                firstError(formErrors.comment)
                                                    ? 'border-red-500'
                                                    : ''
                                            }`}
                                        />
                                        <p className="text-right text-xs text-muted-foreground">
                                            {commentLengthLabel}
                                        </p>
                                        {firstError(formErrors.comment) && (
                                            <p className="text-sm text-red-600">
                                                {firstError(formErrors.comment)}
                                            </p>
                                        )}
                                    </div>
                                </ReviewFieldRow>

                                <ReviewFieldRow
                                    id="item_description_rating"
                                    label="Опис товару"
                                    description="Чи відповідав опис товару реальному стану, мові та версії?"
                                >
                                    <ReviewSelectField
                                        id="item_description_rating"
                                        label="Опис товару"
                                        value={data.item_description_rating}
                                        error={
                                            formErrors.item_description_rating
                                        }
                                        onChange={(value) =>
                                            setData(
                                                'item_description_rating',
                                                value,
                                            )
                                        }
                                        description=""
                                        compact
                                    />
                                </ReviewFieldRow>

                                {!isPickup && (
                                    <ReviewFieldRow
                                        id="packaging_rating"
                                        label="Пакування"
                                        description="Чи був товар упакований безпечно та чи дотримався продавець обраного способу доставки?"
                                    >
                                        <ReviewSelectField
                                            id="packaging_rating"
                                            label="Пакування"
                                            value={data.packaging_rating}
                                            error={formErrors.packaging_rating}
                                            onChange={(value) =>
                                                setData(
                                                    'packaging_rating',
                                                    value,
                                                )
                                            }
                                            description=""
                                            compact
                                        />
                                    </ReviewFieldRow>
                                )}
                            </div>

                            <div className="space-y-4 pt-5">
                                <h3 className="text-base font-semibold">
                                    Скарги
                                </h3>

                                <div className="grid gap-x-10 gap-y-3 sm:grid-cols-2">
                                    {complaintOptions.map((option) => {
                                        const checked =
                                            data.complaints.includes(
                                                option.value,
                                            );

                                        return (
                                            <label
                                                key={option.value}
                                                className="flex items-start gap-3 text-sm leading-6"
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={(
                                                        nextChecked,
                                                    ) =>
                                                        toggleComplaint(
                                                            option.value,
                                                            nextChecked,
                                                        )
                                                    }
                                                    className="mt-1 border-slate-400"
                                                />
                                                <span>{option.label}</span>
                                            </label>
                                        );
                                    })}
                                </div>

                                {complaintsError && (
                                    <p className="text-sm text-red-600">
                                        {complaintsError}
                                    </p>
                                )}
                            </div>

                            {(generalError || data.overall_rating) && (
                                <div className="space-y-3">
                                    {generalError && (
                                        <Alert variant="destructive">
                                            <AlertCircle />
                                            <AlertTitle>
                                                Не вдалося зберегти відгук
                                            </AlertTitle>
                                            <AlertDescription>
                                                {generalError}
                                            </AlertDescription>
                                        </Alert>
                                    )}

                                    {!generalError && (
                                        <p className="text-sm text-muted-foreground">
                                            {selectedRatingsSummary}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="rounded-lg bg-slate-100 p-4 text-sm leading-7 text-slate-700 dark:bg-slate-800/70 dark:text-slate-300">
                            <p>
                                Будь ласка, залишайте об’єктивний відгук і
                                описуйте саме факти, особливо якщо виникла
                                проблема.
                            </p>
                            {!isPickup && (
                                <p className="mt-4">
                                    Оцінюйте товар і пакування окремо. Навіть
                                    якщо сам товар не відповідав очікуванням,
                                    якісне пакування не варто позначати як
                                    погане без причини.
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="border-t px-6 py-5">
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={processing}
                        className="w-full cursor-pointer"
                    >
                        {processing
                            ? 'Відправляємо відгук...'
                            : 'Відправити відгук'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
